import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { 
  Button, 
  Card, 
  TextField, 
  Typography,
  Container,
  Alert,
  CircularProgress,
  Box,
  Paper
} from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import EmailIcon from '@mui/icons-material/Email';
import LoginIcon from '@mui/icons-material/Login';
import UnauthNav from "../../components/UnauthNav/UnauthNav";
import UserApi from "../../api/UserApi";
import "./VerifyUserView.scss";

const VerifyUserView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // URL parameters
  const token = searchParams.get('token') || '';

  // UI state
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  
  // Resend verification state
  const [resendEmail, setResendEmail] = useState<string>('');
  const [resendLoading, setResendLoading] = useState<boolean>(false);
  const [resendSuccess, setResendSuccess] = useState<boolean>(false);
  const [showResendForm, setShowResendForm] = useState<boolean>(false);

  useEffect(() => {
    if (token) {
      verifyAccount();
    } else {
      setLoading(false);
      setError('No verification token provided');
    }
  }, [token]);

  const verifyAccount = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      
      const result = await UserApi.verifyAccount(token);
      setSuccess(true);
      setMessage(result.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async (): Promise<void> => {
    if (!resendEmail.trim()) {
      return;
    }

    try {
      setResendLoading(true);
      
      const result = await UserApi.resendVerification(resendEmail);
      setResendSuccess(true);
    } catch (err) {
      // Handle errors but don't show them to prevent email enumeration
      console.error('Resend verification error:', err);
      setResendSuccess(true); // Always show success for security
    } finally {
      setResendLoading(false);
    }
  };

  const handleLoginRedirect = (): void => {
    navigate('/login');
  };

  return (
    <>
      <UnauthNav />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
            
            {loading && (
              <>
                <CircularProgress size={60} sx={{ mb: 3, color: '#2c5530' }} />
                <Typography variant="h4" gutterBottom color="#2c5530">
                  Verifying Your Account
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Please wait while we verify your email address...
                </Typography>
              </>
            )}

            {!loading && success && (
              <>
                <CheckCircleIcon sx={{ fontSize: 80, color: '#4caf50', mb: 3 }} />
                <Typography variant="h4" gutterBottom color="#2c5530">
                  Email Verified Successfully!
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                  {message}
                </Typography>
                
                <Box sx={{ backgroundColor: '#e8f5e8', p: 3, borderRadius: 2, mb: 3, maxWidth: 500 }}>
                  <Typography variant="h6" color="#2c5530" gutterBottom>
                    🎉 You're All Set!
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your TX Ticket Doctor account is now active. You can:
                  </Typography>
                  <ul style={{ textAlign: 'left', marginTop: 8, color: '#666' }}>
                    <li>Upload your traffic ticket details</li>
                    <li>Choose your legal representation package</li>
                    <li>Track your case progress</li>
                    <li>Communicate with your attorney</li>
                  </ul>
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<LoginIcon />}
                  onClick={handleLoginRedirect}
                  sx={{
                    backgroundColor: '#2c5530',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    '&:hover': { backgroundColor: '#1e3a21' }
                  }}
                >
                  Continue to Login
                </Button>
              </>
            )}

            {!loading && error && (
              <>
                <ErrorIcon sx={{ fontSize: 80, color: '#f44336', mb: 3 }} />
                <Typography variant="h4" gutterBottom color="#d32f2f">
                  Verification Failed
                </Typography>
                <Alert severity="error" sx={{ mb: 3, maxWidth: 500 }}>
                  {error}
                </Alert>
                
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  Don't worry! This can happen if:
                </Typography>
                <ul style={{ textAlign: 'left', marginBottom: 20, color: '#666' }}>
                  <li>The verification link has expired (links are valid for 24 hours)</li>
                  <li>The link was already used</li>
                  <li>There was a problem with the link format</li>
                </ul>

                {!showResendForm && (
                  <Button
                    variant="outlined"
                    startIcon={<EmailIcon />}
                    onClick={() => setShowResendForm(true)}
                    sx={{ 
                      borderColor: '#2c5530', 
                      color: '#2c5530',
                      '&:hover': { borderColor: '#1e3a21', backgroundColor: '#f5f5f5' }
                    }}
                  >
                    Request New Verification Email
                  </Button>
                )}

                {showResendForm && (
                  <Box sx={{ maxWidth: 400, width: '100%', mt: 2 }}>
                    <Typography variant="h6" gutterBottom color="#2c5530">
                      Request New Verification Email
                    </Typography>
                    
                    {!resendSuccess ? (
                      <>
                        <TextField
                          fullWidth
                          type="email"
                          label="Email Address"
                          value={resendEmail}
                          onChange={(e) => setResendEmail(e.target.value)}
                          variant="outlined"
                          sx={{ mb: 2 }}
                          disabled={resendLoading}
                        />
                        
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                          <Button
                            variant="outlined"
                            onClick={() => setShowResendForm(false)}
                            disabled={resendLoading}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleResendVerification}
                            disabled={resendLoading || !resendEmail.trim()}
                            sx={{ backgroundColor: '#2c5530' }}
                          >
                            {resendLoading ? <CircularProgress size={20} /> : 'Send Email'}
                          </Button>
                        </Box>
                      </>
                    ) : (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        If an unverified account with that email exists, a new verification email has been sent.
                      </Alert>
                    )}
                  </Box>
                )}
              </>
            )}

            {/* Help Section */}
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #eee', maxWidth: 500 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Need help? Contact us at{' '}
                <Link to="mailto:support@texasticketdoctor.com" style={{ color: '#2c5530' }}>
                  support@texasticketdoctor.com
                </Link>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Or visit our website at{' '}
                <Link to="https://www.texasticketdoctor.com" target="_blank" style={{ color: '#2c5530' }}>
                  www.texasticketdoctor.com
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default VerifyUserView;